import { useCallback, useEffect, useMemo, useState } from "react";
import { DeleteTwoTone, EditTwoTone, UploadOutlined } from "@ant-design/icons";
import { Button, Space, Upload, message, Row, Col, Typography, Dropdown, Menu, Popconfirm, Modal, Form, Input, Select, Switch } from "antd";
import ListTable from "common/ListTable";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import ExtraContent from "../CommonComponents/ExtraContent";
import FilterComponent from "../CommonComponents/FilterComponent";
import { getData, postData, deleteData, putData } from "../../../apiServices/common";
import {
    SUPPLIER_LIST_ENDPOINT,
    SUPPLIER_ADD_ENDPOINT,
    SUPPLIER_EDIT_ENDPOINT,
    SUPPLIER_DELETE_ENDPOINT,
} from "../../../apiServices/API_ENDPOINTS";
import { useSelector } from "react-redux";
import { hasPermission } from "../../../utils/functions";
import {
  SUPPLIER_CREATE_PERMISSION,
  SUPPLIER_EDIT_PERMISSION,
  SUPPLIER_DELETE_PERMISSION,
} from "../../../routes/permissions";
import { useLocation } from "react-router";

const SupplierConfig = () => {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedSupplierType, setSelectedSupplierType] = useState(null);

  // Antd
  const [supplierForm] = Form.useForm();
  const { Option } = Select;
  const { Title } = Typography;

  // Redux
  const filterData = useSelector(({ filter }) => filter.filterData);

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const getAllSupplier = useCallback(
    async (filterValues) => {
      const { name } = filterValues || {};

      setLoading(true);

      const query = `${SUPPLIER_LIST_ENDPOINT}?page=${currentPage}`;
      const bodyData = {
        ...(name && { name: name }),
      };

      const response = await getData(query, false, bodyData);
      if (response?.status === 200) {
        setLoading(false);
        setMetaData({
          defaultPageSize: response?.data?.data?.per_page,
          current: response?.data?.data?.current_page,
          defaultCurrent: response?.data?.data?.current_page,
          total: response?.data?.data?.total,
        });
        setTableData(response?.data?.data?.data);
      } else {
        setLoading(false);
        message.error("Error getting supplier data");
      }
    },
    [currentPage],
  );

  // Effects
  useEffect(() => {
    getAllSupplier(filterData);
  }, [refresh, filterData, getAllSupplier]);

  const onModalOpen = () => {
    setIsModalOpen(true);
  };

  const onCloseModal = () => {
    supplierForm.resetFields();
    setIsModalOpen(false);
    setIsEdit(false);
  };

  const refetch = () => {
    setRefresh(refresh + 1);
  };

  const deleteSelectedSupplier = async (id) => {
    setLoading(true);
    const response = await deleteData(`${SUPPLIER_DELETE_ENDPOINT}/${id}`);

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Supplier Deleted Successfully");
      refetch();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const editSelectedSupplier = async (formValues) => {
    setLoading(true);

    const bodyData = {
      name: formValues?.name,
      phone: formValues?.phone,
      address: formValues?.address,
      status: formValues?.status,
      local_status: formValues?.local_status,
      short_name: formValues?.short_name,
    };

    const response = await putData(
      `${SUPPLIER_EDIT_ENDPOINT}/${formValues?.id}`,
      bodyData,
    );

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Supplier Edited Successfully");
      refetch();
      supplierForm.resetFields();
      onCloseModal();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const createNewSupplier = async (formValues) => {
    setLoading(true);

    const bodyData = {
      name: formValues?.name,
      phone: formValues?.phone,
      address: formValues?.address,
      status: formValues?.status,
      local_status: formValues?.local_status,
      short_name: formValues?.short_name,
    };

    const response = await postData(SUPPLIER_ADD_ENDPOINT, bodyData);

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Supplier Created Successfully");
      refetch();
      supplierForm.resetFields();
      onCloseModal();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const openModalForEdit = (record) => {
    console.log("UPdate Log", record);
    setIsEdit(true);
    supplierForm.setFieldsValue({
      id: record?.id,
      name: record?.name,
      phone: record?.phone,
      address: record?.address,
      status: record?.status === "1" ? true : false,
      local_status: record?.local_status.toString(),
      short_name: record?.short_name,
    });
    setSelectedSupplierType(record?.local_status.toString());
    setIsModalOpen(true);
  };

  const changePage = (newPage) => {
    setCurrentPage(newPage);
    refetch();
  };

  const tableColumns = [
    {
      title: "Supplier Name",
      dataIndex: ["name"],
      key: "supplier_name",
    },
    {
      title: "Phone",
      dataIndex: ["phone"],
      key: "supplier_phone",
    },
    {
      title: "Address",
      dataIndex: ["address"],
      key: "supplier_address",
    },
    {
      title: "Short Name",
      dataIndex: ["short_name"],
      key: "supplier_short_name",
    },
    {
      title: "Status",
      dataIndex: ["status"],
      key: "supplier_status",
      render: (_, record) => {
        return <div>{record?.status === "1" ? "Active" : "Inactive"}</div>;
      },
    },
    {
      title: "Supplier Type",
      dataIndex: ["local_status"],
      key: "local_status",
      render: (_, record) => {
        return (
          <div>
            {record?.local_status === 1
              ? "Local"
              : record?.local_status === 2
              ? "EPZ"
              : "Foreign"}
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => {
        return (
          <Dropdown.Button
            overlay={
              <Menu
                items={[
                  {
                    label: (
                      <Popconfirm
                        title="Are you sure you want to delete?"
                        onConfirm={() => {
                          deleteSelectedSupplier(record?.id);
                        }}
                      >
                        <span>
                          <DeleteTwoTone />
                        </span>
                        <span
                          style={{
                            color: "red",
                            marginLeft: 5,
                          }}
                        >
                          Delete
                        </span>
                      </Popconfirm>
                    ),
                    key: "1",
                    disabled: !hasPermission([SUPPLIER_DELETE_PERMISSION]),
                  },
                  {
                    label: (
                      <>
                        <span>
                          <EditTwoTone />
                        </span>
                        <span
                          type="button"
                          style={{ marginLeft: 5 }}
                          onClick={() => {
                            openModalForEdit(record);
                          }}
                        >
                          Edit
                        </span>
                      </>
                    ),
                    key: "2",
                    disabled: !hasPermission([SUPPLIER_EDIT_PERMISSION]),
                  },
                ]}
              />
            }
          ></Dropdown.Button>
        );
      },
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={getAllSupplier}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
      />
    ),
    [showFilter, tableData, getAllSupplier],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={onModalOpen}
                disabled={!hasPermission([SUPPLIER_CREATE_PERMISSION])}
                addButtonText={
                  hasPermission([SUPPLIER_CREATE_PERMISSION])
                    ? "Add New"
                    : "No Permission"
                }
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title={<Title level={5}>Supplier Configuration</Title>}
          />
        }
        content={
          <>
            {memoizedCustomFilter}

            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                loading={loading}
                dataSource={tableData}
                columns={tableColumns}
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize,
                  current: metaData?.current,
                  onChange: (newPage) => {
                    changePage(newPage);
                  },
                  defaultCurrent: metaData?.defaultCurrent,
                  total: metaData?.total,
                }}
              />
            </div>
          </>
        }
      />
      <Modal
        open={isModalOpen}
        title={isEdit ? "Edit Supplier" : "Create Supplier"}
        onCancel={() => {
          onCloseModal();
        }}
        maskStyle={{ backdropFilter: "blur(4px)" }}
        footer={null}
      >
        <Form
          form={supplierForm}
          layout="vertical"
          onFinish={isEdit ? editSelectedSupplier : createNewSupplier}
          initialValues={{ local_status: selectedSupplierType }}
        >
          <Row gutter={6}>
            <Col span={12}>
              <Form.Item name="id" hidden />
              <Form.Item
                name="name"
                placeholder="Supplier Name"
                label="Supplier Name"
                rules={[
                  {
                    required: true,
                    message: "Supplier Name Required",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="id" hidden />
              <Form.Item
                name="phone"
                placeholder="Supplier Phone"
                label="Supplier Phone"
                rules={[
                  {
                    required: true,
                    message: "Supplier Phone Required",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={6}>
            <Col span={12}>
              <Form.Item name="id" hidden />
              <Form.Item
                name="address"
                placeholder="Supplier Address"
                label="Supplier Address"
                rules={[
                  {
                    required: true,
                    message: "Supplier Address Required",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="short_name"
                placeholder="Short Name"
                label="Short Name"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={6}>
            <Col span={12}>
              <Form.Item
                name="status"
                placeholder="Status"
                label="Status"
                rules={[
                  {
                    required: true,
                    message: "Status Required",
                  },
                ]}
                valuePropName="checked"
                initialValue={true}
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="local_status"
                placeholder="Supplier Type"
                label="Supplier Type"
                rules={[
                  {
                    required: true,
                    message: "Supplier Type Required",
                  },
                ]}
              >
                <Select
                  placeholder="Select Type"
                  showSearch
                  style={{ width: "100%" }}
                >
                  <Option value="0"> Foreign </Option>
                  <Option value="1"> Local </Option>
                  <Option value="2"> EPZ </Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={6}>
            <Col span={12}>
              <Button
                type="danger"
                style={{ width: "100%" }}
                onClick={() => {
                  onCloseModal();
                }}
              >
                Discard
              </Button>
            </Col>
            <Col span={12}>
              <Button
                type="primary"
                htmlType="submit"
                style={{ width: "100%" }}
                loading={loading}
              >
                Save
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default SupplierConfig;
